import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import AdaptivePopup from "../../../AdaptivePopup/AdaptivePopup";
import "./OrdersScanPopup.css";

function extractOrderId(rawValue) {
  if (!rawValue) return null;
  const trimmed = rawValue.trim();

  const urlMatch = trimmed.match(/orders\/(item-v2|item)\/([A-Za-z0-9]+)/i);
  if (urlMatch) {
    return urlMatch[2];
  }

  const directId = trimmed.match(/([a-f0-9]{24})/i);
  if (directId) {
    return directId[1];
  }

  return null;
}

function OrdersScanPopup({ isOpen, onClose, onDetect }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const frameRequestRef = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return undefined;

    let detector = null;
    let isActive = true;

    async function startScanner() {
      setError("");

      const hasBarcodeDetector = "BarcodeDetector" in window;

      if (hasBarcodeDetector) {
        try {
          detector = new window.BarcodeDetector({ formats: ["qr_code"] });
        } catch (err) {
          setError("Не удалось инициализировать сканер: " + err.message);
          return;
        }
      } else {
        // jsQR fallback is always available via static import
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });

        if (!isActive) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        detectLoop({ detector, useFallback: !hasBarcodeDetector });
      } catch (err) {
        setError("Не удалось получить доступ к камере: " + err.message);
      }
    }

    async function detectLoop({ detector, useFallback }) {
      if (!isActive || !videoRef.current) return;

      if (useFallback) {
        try {
          const video = videoRef.current;
          const canvas = canvasRef.current;
          if (!canvas) return;
          const ctx = canvas.getContext("2d", { willReadFrequently: true });
          canvas.width = video.videoWidth || 640;
          canvas.height = video.videoHeight || 480;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const result = jsQR(imageData.data, canvas.width, canvas.height);
          if (result && result.data) {
            const orderId = extractOrderId(result.data);
            if (orderId && onDetect) {
              onDetect(orderId);
              return;
            }
          }
        } catch (err) {
          setError("Ошибка сканирования: " + err.message);
        }
      } else if (detector) {
        try {
          const results = await detector.detect(videoRef.current);
          if (results && results.length > 0) {
            const orderId = extractOrderId(results[0].rawValue);
            if (orderId && onDetect) {
              onDetect(orderId);
              return;
            }
          }
        } catch (err) {
          setError("Ошибка сканирования: " + err.message);
        }
      }

      frameRequestRef.current = requestAnimationFrame(() => detectLoop({ detector, useFallback }));
    }

    startScanner();

    return () => {
      isActive = false;
      if (frameRequestRef.current) {
        cancelAnimationFrame(frameRequestRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, [isOpen, onDetect]);

  return (
    <AdaptivePopup isOpen={isOpen} handleClose={onClose} popupName="orders-scan-popup">
      <div className="orders-scan">
        <div className="orders-scan__header">
          <h3 className="orders-scan__title">Сканирование QR-кода</h3>
          <button type="button" className="orders-scan__close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="orders-scan__video-wrap">
          <video
            ref={videoRef}
            className="orders-scan__video"
            muted
            playsInline
            autoPlay
          />
          <canvas ref={canvasRef} className="orders-scan__canvas" />
        </div>

        {error ? (
          <p className="orders-scan__error">{error}</p>
        ) : (
          <p className="orders-scan__hint">Наведите камеру на QR-код заказа</p>
        )}
      </div>
    </AdaptivePopup>
  );
}

export default OrdersScanPopup;
